import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Package, Plus, Upload } from 'lucide-react';
import { listCustomerCatalog, importCustomerItems } from '../../api/catalog';
import { CatalogItemThumbnail } from '../../components/CatalogItemThumbnail';
import { CsvImportModal } from '../../components/CsvImportModal';
import { Card } from '../../components/ui/Card';
import { Table, Thead, Th, Tr, Td } from '../../components/ui/Table';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { EmptyState } from '../../components/ui/EmptyState';
import { Button } from '../../components/ui/Button';
import { CatalogItemModal } from './CatalogItemModal';
import type { CustomerCatalogItem, CustomerSite } from '../../types/api';

function money(value: string) {
  return `£${Number(value).toFixed(2)}`;
}

export function CustomerCatalogTab({ orgId, sites }: { orgId: string; sites: CustomerSite[] }) {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<CustomerCatalogItem | null>(null);

  const catalogQuery = useQuery({ queryKey: ['catalog', orgId], queryFn: () => listCustomerCatalog(orgId) });

  function openNew() {
    setEditingItem(null);
    setModalOpen(true);
  }

  function openEdit(item: CustomerCatalogItem) {
    setEditingItem(item);
    setModalOpen(true);
  }

  return (
    <>
      <Card>
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <span className="text-sm font-medium text-slate-600">
            {catalogQuery.data?.length ?? 0} item{catalogQuery.data?.length === 1 ? '' : 's'}
          </span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="secondary" onClick={() => setImportOpen(true)}>
              <Upload className="h-3.5 w-3.5" /> Import CSV
            </Button>
            <Button size="sm" onClick={openNew}>
              <Plus className="h-3.5 w-3.5" /> New item
            </Button>
          </div>
        </div>
        {catalogQuery.isLoading ? (
          <div className="py-14 text-center">
            <Spinner />
          </div>
        ) : !catalogQuery.data || catalogQuery.data.length === 0 ? (
          <EmptyState
            icon={Package}
            title="No catalog items yet"
            description="Add items this customer can order, either from scratch or based on a master item."
          />
        ) : (
          <Table>
            <Thead>
              <tr>
                <Th></Th>
                <Th>Name</Th>
                <Th>Availability</Th>
                <Th>Price</Th>
                <Th>Charge type</Th>
                <Th>Visible</Th>
                <Th />
              </tr>
            </Thead>
            <tbody>
              {catalogQuery.data.map((item) => (
                <Tr key={item.id}>
                  <Td>
                    <CatalogItemThumbnail item={item} size="sm" />
                  </Td>
                  <Td className="font-medium text-slate-900">
                    {item.name}
                    {item.category && <span className="ml-2 text-xs font-normal text-slate-400">{item.category}</span>}
                  </Td>
                  <Td>{item.site ? item.site.name : <span className="text-slate-400">Org-wide</span>}</Td>
                  <Td>{money(item.price)}</Td>
                  <Td>
                    {item.chargeTypeOverride ? (
                      <Badge tone="blue">{item.chargeTypeOverride.replace('_', '-')}</Badge>
                    ) : (
                      <span className="text-slate-400">Default</span>
                    )}
                  </Td>
                  <Td>{item.visible ? <Badge tone="green">Visible</Badge> : <Badge>Hidden</Badge>}</Td>
                  <Td className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(item)}>
                      Edit
                    </Button>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <CatalogItemModal
        orgId={orgId}
        sites={sites}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        item={editingItem}
      />
      <CsvImportModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        title="Import catalog items"
        description={`Add or update this customer's catalog items in bulk. "Site" is optional and must match an existing site name exactly (blank = org-wide). Matches existing items by name + site, so re-running an updated file is safe.`}
        templateFilename="customer-catalog-template.csv"
        templateHeaders={['name', 'description', 'category', 'price', 'site', 'visible']}
        templateExampleRow={['Dell Latitude 5440', '14" business laptop', 'Laptop', '850', sites[0]?.name ?? '', 'true']}
        importFn={(file) => importCustomerItems(orgId, file)}
        onImported={() => queryClient.invalidateQueries({ queryKey: ['catalog', orgId] })}
      />
    </>
  );
}
